/**
 * /settings/pos/sessions — historical till sessions with reconciliation totals
 * and per-transaction drill-down drawer.
 *
 * Sub-spec 02 Plan 7 M9 (Task 25).
 *
 * Gate: POS is Pro+. Renders RequireFeature around the main body so Starter
 * tenants see the upgrade prompt rather than the session list.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { RequireFeature } from '@/components/RequireFeature';
import { ApiError, getPosSessionDetail, listPosSessions, reprintPosTransaction } from '@/lib/api';
import { getPairedPrinter, printBytes, renderZReport } from '@/lib/escpos';
import type { PosSession, PosTransaction } from '@/lib/types';
import { ClipboardList, ShoppingCart, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function formatMoney(minor: number, currency: string): string {
  const sym = CURRENCY_SYMBOLS[currency] ?? currency;
  return `${sym}${(minor / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  return iso.replace('T', ' ').slice(0, 16);
}

type StatusFilter = 'open' | 'closed' | 'all';

// ---------------------------------------------------------------------------
// Variance pill
// ---------------------------------------------------------------------------

interface VariancePillProps {
  varianceMinor: number | null;
  currency: string;
}

function VariancePill({ varianceMinor, currency }: VariancePillProps) {
  if (varianceMinor === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (varianceMinor === 0) {
    return <span className="num text-muted-foreground">{formatMoney(0, currency)}</span>;
  }
  if (varianceMinor > 0) {
    return (
      <span className="num font-medium text-success">+{formatMoney(varianceMinor, currency)}</span>
    );
  }
  return (
    <span className="num font-medium text-destructive">{formatMoney(varianceMinor, currency)}</span>
  );
}

// ---------------------------------------------------------------------------
// Session detail drawer
// ---------------------------------------------------------------------------

type DetailData = {
  session: PosSession;
  transactions: PosTransaction[];
  totals_by_source: Record<string, number>;
  /** Migration 0098 — card gratuities in this session (already inside stripe_card). */
  tips_minor?: number;
};

interface DetailDrawerProps {
  sessionId: string;
  accountName: string;
  facilityName: string;
  currency: string;
  onClose: () => void;
}

function DetailDrawer({
  sessionId,
  accountName,
  facilityName,
  currency,
  onClose,
}: DetailDrawerProps) {
  const [detail, setDetail] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTxnId, setExpandedTxnId] = useState<string | null>(null);
  const [printerPaired, setPrinterPaired] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await getPosSessionDetail(sessionId);
        if (!cancelled) setDetail(data);
      } catch {
        if (!cancelled) setError('Failed to load session detail.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Check whether a printer is paired on mount.
  useEffect(() => {
    async function checkPrinter() {
      try {
        const result = await getPairedPrinter();
        setPrinterPaired(result !== null);
      } catch {
        setPrinterPaired(false);
      }
    }
    void checkPrinter();
  }, []);

  async function handlePrintZReport() {
    if (!detail) return;
    const { session, totals_by_source } = detail;
    if (!session.closedAt || session.closingFloatMinor === null) return;

    setPrinting(true);
    setPrintError(null);
    try {
      const bytes = renderZReport(
        { name: accountName },
        { name: facilityName, addressLine: '' },
        {
          opened_at: session.openedAt,
          closed_at: session.closedAt,
          opening_float_minor: session.openingFloatMinor,
          closing_float_minor: session.closingFloatMinor,
          expected_cash_minor: session.expectedCashMinor ?? 0,
          cash_variance_minor: session.cashVarianceMinor ?? 0,
          totals_by_source,
          currency,
        },
      );
      await printBytes(bytes);
    } catch (err) {
      setPrintError(err instanceof Error ? err.message : 'Print failed');
    } finally {
      setPrinting(false);
    }
  }

  function handleExportCsv() {
    window.location.href = `/api/pos/sessions/${sessionId}/export.csv`;
  }

  const session = detail?.session;

  return (
    <dialog
      className="fixed inset-0 z-50 flex justify-end bg-foreground/40 p-0"
      aria-label="Session detail"
      open
    >
      <div className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-surface p-6 ring-1 ring-border elevate">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">Session detail</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {loading && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-10 animate-pulse rounded-lg bg-secondary"
                style={{ opacity: 1 - i * 0.25 }}
              />
            ))}
          </div>
        )}

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        {detail !== null && session !== undefined && (
          <>
            {/* Session header */}
            <div className="mb-5 space-y-2 rounded-xl bg-secondary/60 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Opened</span>
                <span className="num font-medium">{formatDate(session.openedAt)}</span>
              </div>
              {session.closedAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Closed</span>
                  <span className="num font-medium">{formatDate(session.closedAt)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Opening float</span>
                <span className="num font-medium">
                  {formatMoney(session.openingFloatMinor, currency)}
                </span>
              </div>
              {session.closingFloatMinor !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Closing float</span>
                  <span className="num font-medium">
                    {formatMoney(session.closingFloatMinor, currency)}
                  </span>
                </div>
              )}
              {session.expectedCashMinor !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected cash</span>
                  <span className="num font-medium">
                    {formatMoney(session.expectedCashMinor, currency)}
                  </span>
                </div>
              )}
              {session.cashVarianceMinor !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Variance</span>
                  <VariancePill varianceMinor={session.cashVarianceMinor} currency={currency} />
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium capitalize">{session.status}</span>
              </div>
            </div>

            {/* Tips (migration 0098) */}
            {(detail.tips_minor ?? 0) > 0 && (
              <div className="mb-5 flex justify-between rounded-xl bg-secondary/60 px-4 py-3 text-sm">
                <span className="text-muted-foreground">Tips taken on card</span>
                <span className="num font-medium" data-testid="session-tips">
                  {formatMoney(detail.tips_minor ?? 0, currency)}
                </span>
              </div>
            )}

            {/* Totals by source */}
            {Object.keys(detail.totals_by_source).length > 0 && (
              <section className="mb-5">
                <h3 className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Totals by source
                </h3>
                <div className="overflow-hidden rounded-xl bg-surface ring-1 ring-border">
                  {Object.entries(detail.totals_by_source).map(([source, amount]) => (
                    <div
                      key={source}
                      className="flex items-center justify-between border-b border-border px-4 py-2.5 text-sm last:border-0"
                    >
                      <span className="capitalize text-muted-foreground">{source}</span>
                      <span className="num font-medium">{formatMoney(amount, currency)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Transactions */}
            <section className="mb-5">
              <h3 className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Transactions ({detail.transactions.length})
              </h3>
              {detail.transactions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No transactions in this session.</p>
              ) : (
                <div className="overflow-hidden rounded-xl bg-surface ring-1 ring-border">
                  {detail.transactions.map((txn) => (
                    <div key={txn.id} className="border-b border-border last:border-0">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedTxnId((prev) => (prev === txn.id ? null : txn.id))
                        }
                        className="flex w-full items-center justify-between px-4 py-3 text-sm hover:bg-secondary/40 press"
                      >
                        <span>
                          <span className="num text-[11px] text-muted-foreground">
                            {txn.id.slice(-8).toUpperCase()}
                          </span>
                          <span className="ml-2 capitalize text-muted-foreground">
                            {txn.status}
                          </span>
                        </span>
                        <span className="num font-medium">
                          {formatMoney(txn.totalMinor, txn.currency)}
                        </span>
                      </button>

                      {expandedTxnId === txn.id && (
                        <div className="border-t border-border bg-secondary/20 px-4 py-3 text-xs text-muted-foreground">
                          <p className="num">{txn.id}</p>
                          <p className="mt-1">{formatDate(txn.createdAt)}</p>
                          {txn.status !== 'pending' && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void (async () => {
                                  try {
                                    const r = await reprintPosTransaction(txn.id);
                                    const w = window.open(
                                      '',
                                      '_blank',
                                      'noopener,noreferrer,popup=yes,width=420,height=720',
                                    );
                                    if (!w) return;
                                    const doc = w.document;
                                    doc.title = `Receipt ${r.transaction.id.slice(-8)}`;
                                    const sym =
                                      CURRENCY_SYMBOLS[r.transaction.currency] ??
                                      r.transaction.currency;
                                    const h2 = doc.createElement('h2');
                                    h2.style.fontFamily = 'system-ui';
                                    h2.style.fontSize = '14px';
                                    h2.style.textAlign = 'center';
                                    h2.textContent = `Receipt #${r.transaction.id.slice(-8).toUpperCase()}`;
                                    doc.body.appendChild(h2);
                                    const tbl = doc.createElement('table');
                                    tbl.style.width = '100%';
                                    tbl.style.fontFamily = 'monospace';
                                    tbl.style.fontSize = '12px';
                                    tbl.style.borderCollapse = 'collapse';
                                    for (const l of r.lines) {
                                      const tr = doc.createElement('tr');
                                      const td1 = doc.createElement('td');
                                      td1.textContent = `${l.quantity}× ${l.description}`;
                                      const td2 = doc.createElement('td');
                                      td2.style.textAlign = 'right';
                                      td2.textContent = `${sym}${(l.line_total_minor / 100).toFixed(2)}`;
                                      tr.appendChild(td1);
                                      tr.appendChild(td2);
                                      tbl.appendChild(tr);
                                    }
                                    const trTot = doc.createElement('tr');
                                    const tdTot = doc.createElement('td');
                                    tdTot.colSpan = 2;
                                    tdTot.style.borderTop = '1px solid #000';
                                    tdTot.style.paddingTop = '6px';
                                    tdTot.style.textAlign = 'right';
                                    const b = doc.createElement('strong');
                                    b.textContent = `Total: ${sym}${(r.transaction.total_minor / 100).toFixed(2)}`;
                                    tdTot.appendChild(b);
                                    trTot.appendChild(tdTot);
                                    tbl.appendChild(trTot);
                                    doc.body.appendChild(tbl);
                                    const note = doc.createElement('p');
                                    note.style.fontFamily = 'system-ui';
                                    note.style.fontSize = '10px';
                                    note.style.color = '#666';
                                    note.style.marginTop = '12px';
                                    note.textContent = `Reprint #${r.reprint_count}`;
                                    doc.body.appendChild(note);
                                    w.print();
                                  } catch (err) {
                                    console.warn('reprint failed', err);
                                  }
                                })();
                              }}
                              className="mt-2 rounded-md bg-secondary px-2 py-1 text-xs font-medium hover:bg-secondary/80 press"
                              data-testid={`reprint-btn-${txn.id}`}
                            >
                              Reprint receipt
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {printError && (
              <p className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {printError}
              </p>
            )}
          </>
        )}

        {/* Footer actions */}
        <div className="mt-auto flex flex-wrap gap-2 border-t border-border pt-4">
          {printerPaired && detail?.session.status === 'closed' && (
            <button
              type="button"
              disabled={printing}
              onClick={() => void handlePrintZReport()}
              className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/80 press disabled:opacity-50"
            >
              {printing ? 'Printing…' : 'Print Z report'}
            </button>
          )}
          <button
            type="button"
            onClick={handleExportCsv}
            className="rounded-md bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/80 press"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="ml-auto px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Sessions list body
// ---------------------------------------------------------------------------

function PosSessionsBody() {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const facilities = state.me?.facilitiesInScope ?? [];
  const currency = account?.currency ?? 'GBP';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('closed');
  const [facilityFilter, setFacilityFilter] = useState<string>('');
  const [sessions, setSessions] = useState<PosSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const filter: { status?: 'open' | 'closed'; facility_id?: string } = {};
      if (statusFilter !== 'all') filter.status = statusFilter;
      if (facilityFilter) filter.facility_id = facilityFilter;
      const res = await listPosSessions(filter);
      setSessions(res.sessions);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 402)) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, facilityFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const viewingSession = sessions.find((s) => s.id === viewingId) ?? null;
  const viewingFacility =
    viewingSession !== null
      ? (facilities.find((f) => f.id === viewingSession.facilityId) ?? null)
      : null;

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to view POS sessions.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access, or upgrade to a Pro plan.
        </p>
      </div>
    );
  }

  const STATUS_OPTIONS: Array<{ label: string; value: StatusFilter }> = [
    { label: 'Closed', value: 'closed' },
    { label: 'Open', value: 'open' },
    { label: 'All', value: 'all' },
  ];

  return (
    <>
      {/* Filter bar */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        {/* Status pills */}
        <div className="flex gap-1">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors press ${
                statusFilter === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Facility filter */}
        {facilities.length > 1 && (
          <select
            value={facilityFilter}
            onChange={(e) => setFacilityFilter(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">All facilities</option>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <ClipboardList className="mx-auto mb-3 size-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium text-foreground">
            No {statusFilter !== 'all' ? statusFilter : ''} sessions found.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Sessions appear here after you open a till at the POS.
          </p>
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Opened
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Closed
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Opening float
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Closing float
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Variance
                  </th>
                  <th className="px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    Status
                  </th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sessions.map((s) => (
                  <tr key={s.id} className="hover:bg-secondary/30">
                    <td className="num px-5 py-3.5 text-muted-foreground">
                      {formatDate(s.openedAt)}
                    </td>
                    <td className="num px-5 py-3.5 text-muted-foreground">
                      {s.closedAt ? formatDate(s.closedAt) : '—'}
                    </td>
                    <td className="num px-5 py-3.5">
                      {formatMoney(s.openingFloatMinor, currency)}
                    </td>
                    <td className="num px-5 py-3.5">
                      {s.closingFloatMinor !== null
                        ? formatMoney(s.closingFloatMinor, currency)
                        : '—'}
                    </td>
                    <td className="px-5 py-3.5">
                      <VariancePill varianceMinor={s.cashVarianceMinor} currency={currency} />
                    </td>
                    <td className="px-5 py-3.5">
                      {s.status === 'open' ? (
                        <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
                          Open
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
                          Closed
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        type="button"
                        onClick={() => setViewingId(s.id)}
                        className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-secondary press"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {viewingId !== null && (
        <DetailDrawer
          sessionId={viewingId}
          accountName={account?.name ?? 'Your Kennel'}
          facilityName={viewingFacility?.name ?? ''}
          currency={currency}
          onClose={() => setViewingId(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PosSessionsSettings() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Till sessions</h1>
        <p className="mt-1 max-w-xl text-sm text-muted-foreground">
          Historical till sessions with reconciliation totals and per-transaction drill-down.
        </p>
      </header>

      {/* Quick action — navigate to POS to open a new till */}
      <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3.5">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <ShoppingCart className="size-4 text-primary" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">Want to start a new session?</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Go to the POS page — you'll be prompted to open a till with an opening float.
          </p>
        </div>
        <a
          href="/pos"
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press"
        >
          Open POS
        </a>
      </div>

      <RequireFeature flag="pos">
        <PosSessionsBody />
      </RequireFeature>
    </div>
  );
}
